// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { parseCsv, detectColumns } from '../../lib/import/csv-parser.js';
import type { ColumnMapping } from '../../lib/import/csv-parser.js';
import { detectDuplicates } from '../../lib/import/duplicate-detector.js';
import { applyPayeeSuggestions } from '../../lib/import/payee-matcher.js';
import { buildTransactionPayload, findImbalanceAccount } from '../../lib/import/pipeline.js';
import type { ReviewedTransaction } from '../../lib/import/types.js';
import { buildEvent, validateSplitsBalance } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { ImportReviewTable } from './ImportReviewTable.js';

interface Props {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  preselectedAccountId?: string;
}

type Step = 'select' | 'map' | 'review' | 'import' | 'done';

export function CsvImport({ worker, keyData, preselectedAccountId }: Props) {
  const { t } = useTranslation(['import', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const [step, setStep] = useState<Step>('select');
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [accountId, setAccountId] = useState(preselectedAccountId ?? '');
  const [mapping, setMapping] = useState<Partial<ColumnMapping>>({});
  const [dateFormat, setDateFormat] = useState<'MDY' | 'DMY'>('MDY');
  const [flipSign, setFlipSign] = useState(false);
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<Record<string, string>[]>([]);
  const [reviewed, setReviewed] = useState<ReviewedTransaction[]>([]);
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [error, setError] = useState('');

  const accounts = [...ledger.accounts.values()].filter((a) => !a.hidden && !a.placeholder);

  const handleFile = useCallback((file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setFileContent(content);
      // Quick header peek for column mapping
      const firstLine = content.split('\n')[0] ?? '';
      // Strip BOM
      const cleaned = firstLine.startsWith('﻿') ? firstLine.slice(1) : firstLine;
      const headers = cleaned.split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
      setPreviewHeaders(headers);
      // Preview first 5 rows
      import('papaparse').then(({ default: Papa }) => {
        const result = Papa.parse<Record<string, string>>(content.startsWith('﻿') ? content.slice(1) : content, {
          header: true,
          preview: 5,
        });
        setPreviewRows(result.data);
        setMapping(detectColumns(headers));
      }).catch(() => {});
      setStep('map');
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFilePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleParse = () => {
    if (!accountId) { setError(t('selectTargetAccount')); return; }
    setError('');
    const result = parseCsv(fileContent, {
      accountId,
      mapping,
      dateFormatOverride: dateFormat,
      flipSign,
    });
    if (result.errors.length > 0 && result.transactions.length === 0) {
      setError(t('csv.parseFailed', { message: result.errors[0]?.message ?? 'Unknown error' }));
      return;
    }
    const withDup = detectDuplicates(result.transactions, ledger);
    setReviewed(applyPayeeSuggestions(withDup, ledger));
    setStep('review');
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');
    const sessionId = uuidv4();
    let imbalanceAccountId = findImbalanceAccount(ledger, 'USD') ?? '';
    if (!imbalanceAccountId) {
      // Use first expense account as fallback imbalance
      const expense = [...ledger.accounts.values()].find((a) => a.type === 'EXPENSE');
      imbalanceAccountId = expense?.id ?? accountId;
    }

    const toImport = reviewed.filter((t) => t.selected);
    let count = 0;
    const nextSeq = ledger.lastSequence + 1;

    for (let i = 0; i < toImport.length; i++) {
      const tx = toImport[i]!;
      try {
        const payload = buildTransactionPayload(tx, sessionId, ledger, imbalanceAccountId);
        validateSplitsBalance(payload.splits);
        const event = await buildEvent('TransactionPosted', payload, nextSeq + i);
        await encryptAndPush(event);
        count++;
        setImportedCount(count);
      } catch (err) {
        setError(t('errorOnTransaction', { index: i + 1, message: err instanceof Error ? err.message : String(err) }));
        setImporting(false);
        return;
      }
    }

    setImporting(false);
    setStep('done');
  };

  if (step === 'select') {
    return (
      <div className="space-y-6">
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-border p-12 text-center hover:border-accent transition-colors"
        >
          <p className="font-mono text-2xl text-accent mb-3">CSV</p>
          <p className="text-text-muted text-sm mb-4">{t('csv.dropZone')}</p>
          <label className="px-4 py-2 bg-accent text-white text-sm cursor-pointer hover:bg-accent-hover">
            {t('csv.chooseFile')}
            <input type="file" accept=".csv,.txt" className="hidden" onChange={handleFilePick} />
          </label>
        </div>
      </div>
    );
  }

  if (step === 'map') {
    return (
      <div className="space-y-6">
        <div className="text-xs text-text-muted">{t('csv.file')} <span className="font-mono text-text">{fileName}</span></div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-text-muted mb-1">{t('csv.targetAccount')}</label>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
            >
              <option value="">{t('selectAccount')}</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1">{t('csv.dateFormat')}</label>
            <select
              value={dateFormat}
              onChange={(e) => setDateFormat(e.target.value as 'MDY' | 'DMY')}
              className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
            >
              <option value="MDY">{t('csv.dateFormatUs')}</option>
              <option value="DMY">{t('csv.dateFormatUk')}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1">{t('csv.dateColumn')}</label>
            <select
              value={mapping.date ?? ''}
              onChange={(e) => setMapping((m) => ({ ...m, date: e.target.value || null }))}
              className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
            >
              <option value="">{t('csv.autoDetect')}</option>
              {previewHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1">{t('csv.descriptionColumn')}</label>
            <select
              value={mapping.description ?? ''}
              onChange={(e) => setMapping((m) => ({ ...m, description: e.target.value || null }))}
              className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
            >
              <option value="">{t('csv.autoDetect')}</option>
              {previewHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1">{t('csv.amountColumn')}</label>
            <select
              value={mapping.amount ?? ''}
              onChange={(e) => setMapping((m) => ({ ...m, amount: e.target.value || null }))}
              className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
            >
              <option value="">{t('csv.autoDetect')}</option>
              {previewHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2 pt-6">
            <input
              type="checkbox"
              id="flip-sign"
              checked={flipSign}
              onChange={(e) => setFlipSign(e.target.checked)}
            />
            <label htmlFor="flip-sign" className="text-sm text-text-muted">
              {t('csv.flipSign')}
            </label>
          </div>
        </div>

        {previewRows.length > 0 && (
          <div className="overflow-x-auto">
            <p className="text-xs text-text-muted mb-2 uppercase tracking-wide">{t('csv.preview')}</p>
            <table className="text-xs border-collapse">
              <thead>
                <tr>
                  {previewHeaders.map((h) => (
                    <th key={h} className="border border-border px-2 py-1 bg-bg-elevated text-text-muted text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {previewHeaders.map((h) => (
                      <td key={h} className="border border-border px-2 py-1 text-text font-mono">{row[h] ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {error && <p className="text-negative text-sm">{error}</p>}

        <div className="flex gap-3">
          <button onClick={() => setStep('select')} className="px-4 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">{t('common:actions.back')}</button>
          <button onClick={handleParse} className="px-4 py-2 bg-accent text-white text-sm hover:bg-accent-hover transition-colors">
            {t('ofx.previewImport')}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    const toImport = reviewed.filter((t) => t.selected);
    return (
      <div className="space-y-4">
        <ImportReviewTable transactions={reviewed} accounts={accounts} onChange={setReviewed} />
        {error && <p className="text-negative text-sm">{error}</p>}
        <div className="flex gap-3">
          <button onClick={() => setStep('map')} className="px-4 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">{t('common:actions.back')}</button>
          <button
            onClick={() => { void handleImport(); }}
            disabled={toImport.length === 0 || importing}
            className="px-4 py-2 bg-accent text-white text-sm hover:bg-accent-hover disabled:opacity-50 transition-colors"
          >
            {importing ? t('importingProgress', { current: importedCount, total: toImport.length }) : t('importCount', { count: toImport.length })}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="text-center space-y-4 py-8">
        <p className="font-mono text-positive text-2xl">✓</p>
        <p className="text-md font-medium text-text">{t('importedCount', { count: importedCount })}</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate(`/accounts/${accountId}`)} className="px-4 py-2 bg-accent text-white text-sm hover:bg-accent-hover transition-colors">
            {t('viewAccountRegister')}
          </button>
          <button onClick={() => { setStep('select'); setFileContent(''); setFileName(''); }} className="px-4 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">
            {t('importAnotherFile')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
